import { useState } from 'react';
import { CompactSegmentedToggle } from '../../billing/overview/CompactSegmentedToggle';
import { BreakdownCard } from './BreakdownCard';
import { sumCounts } from './overviewFormat';
import {
    BREAKDOWN_MODE_OPTIONS,
    breakdownSubtitle,
    type BreakdownMode,
    type HistoryView
} from './overviewPeriod';
import { DonutBreakdown, type PieSlice } from './OverviewDonut';
import { HistoryFallback } from './OverviewStates';
import { EmptyNote } from './StackedBar';

const MODE_COPY: Readonly<Record<BreakdownMode, { caption: string; empty: string }>> = {
    open: { caption: 'open', empty: 'No open notifications.' },
    range: { caption: 'raised', empty: 'No notifications raised in this period.' }
};

interface NotificationBreakdownCardProps {
    title: string;
    info: Readonly<Record<BreakdownMode, string>>;
    openSlices: PieSlice[];
    // Null until the range has loaded.
    rangeSlices: PieSlice[] | null;
    history: HistoryView;
}

function BreakdownBody({
    slices,
    label,
    mode
}: {
    slices: PieSlice[];
    label: string;
    mode: BreakdownMode;
}) {
    const total = sumCounts(slices);
    const copy = MODE_COPY[mode];
    if (total === 0) return <EmptyNote>{copy.empty}</EmptyNote>;
    return (
        <DonutBreakdown
            slices={slices}
            label={label}
            center={{ value: total.toLocaleString(), caption: copy.caption }}
        />
    );
}

export function NotificationBreakdownCard({
    title,
    info,
    openSlices,
    rangeSlices,
    history
}: NotificationBreakdownCardProps) {
    const [mode, setMode] = useState<BreakdownMode>('open');
    const slices = mode === 'open' ? openSlices : rangeSlices;
    return (
        <BreakdownCard
            title={title}
            info={info[mode]}
            subtitle={breakdownSubtitle(mode, history.period)}
            control={
                <CompactSegmentedToggle
                    active={mode}
                    options={BREAKDOWN_MODE_OPTIONS}
                    onChange={setMode}
                    ariaLabel={`${title}: open or in range`}
                />
            }
        >
            {slices ? (
                <BreakdownBody slices={slices} label={title} mode={mode} />
            ) : (
                <HistoryFallback failed={history.failed} />
            )}
        </BreakdownCard>
    );
}
